///
/// EarthBrowser
///
/// Created by Matt Giger on September 16, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Shown when a sign-in was blocked by the device seat limit. The blocked
/// sign-in already verified the user's identity, so the server issued a
/// short-lived eviction token (delivered in the 409 body for passkey
/// sign-ins, or via the ?deviceLimit= redirect param for OAuth). The dialog
/// lists the account's devices, lets the user sign one out, then sends them
/// back to the sign-in dialog to retry.
///

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { toast } from "@/components/ui/use-toast"
import { setAuthOpen, setDeviceLimitToken } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector, type RootState } from "@/redux/store"
import { formatDistanceToNow } from "date-fns"
import { Loader2, MonitorSmartphone } from "lucide-react"
import { useEffect, useState } from "react"

interface EvictableDevice {
	id: number
	name: string
	os: string
	lastUsed?: string
}

function lastUsedText(device: EvictableDevice): string {
	if (!device.lastUsed) return "Unknown"
	try {
		return formatDistanceToNow(new Date(device.lastUsed), { addSuffix: true })
	} catch {
		return "Unknown"
	}
}

export function DeviceLimitDialog() {
	const dispatch = useAppDispatch()
	const token = useAppSelector((state: RootState) => state.dialog.deviceLimitToken)

	const [devices, setDevices] = useState<EvictableDevice[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [evictingId, setEvictingId] = useState<number | null>(null)
	const [evictedCount, setEvictedCount] = useState(0)

	function close() {
		dispatch(setDeviceLimitToken(null))
	}

	// Fetch the account's devices when the dialog opens
	useEffect(() => {
		if (!token) {
			setDevices([])
			setEvictedCount(0)
			return
		}

		let cancelled = false
		setIsLoading(true)

		const fetchDevices = async () => {
			try {
				const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/devices/evict`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ token }),
				})
				if (!response.ok) {
					const error = await response.json() as { message?: string }
					throw new Error(error.message || "Failed to load devices")
				}
				const data = await response.json() as { devices: EvictableDevice[] }
				if (!cancelled) {
					setDevices(data.devices)
				}
			} catch (error) {
				if (!cancelled) {
					dispatch(setDeviceLimitToken(null))
					toast({
						title: "Couldn't load devices",
						variant: "destructive",
						description: error instanceof Error ? error.message : "Please try signing in again.",
					})
				}
			} finally {
				if (!cancelled) {
					setIsLoading(false)
				}
			}
		}
		void fetchDevices()

		return () => { cancelled = true }
	}, [token, dispatch])

	async function handleEvict(device: EvictableDevice) {
		if (!token) return
		setEvictingId(device.id)

		try {
			const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/devices/evict`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ token, deviceId: device.id }),
			})
			if (!response.ok) {
				const error = await response.json() as { message?: string }
				throw new Error(error.message || "Failed to sign out device")
			}

			// Seat freed: drop the row but keep the dialog open so accounts
			// that are over the limit by more than one can free several
			// seats before retrying
			setDevices(current => current.filter(d => d.id !== device.id))
			setEvictedCount(count => count + 1)
		} catch (error) {
			toast({
				title: "Failed to sign out device",
				variant: "destructive",
				description: error instanceof Error ? error.message : "Please try again.",
			})
		} finally {
			setEvictingId(null)
		}
	}

	function continueToSignIn() {
		dispatch(setDeviceLimitToken(null))
		dispatch(setAuthOpen(true))
	}

	return (
		<Dialog open={token !== null} onOpenChange={(open) => { if (!open) close() }}>
			<DialogContent
				className="grid gap-4 bg-black/40 dark:bg-black/40 backdrop-blur-xl border border-white/20 dark:border-white/20 text-white shadow-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto"
				aria-describedby=""
			>
				<DialogHeader className="space-y-1">
					<DialogTitle className="text-2xl text-white">Device limit reached</DialogTitle>
					<DialogDescription className="text-white/60">
						Your account is signed in on the maximum number of devices.
						Sign one out to continue on this device.
					</DialogDescription>
				</DialogHeader>

				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<Loader2 className="h-6 w-6 animate-spin text-white/60" />
					</div>
				) : (
					<div className="grid gap-2">
						{devices.map((device) => (
							<div
								key={device.id}
								className="flex items-center gap-3 rounded-xl border border-white/15 bg-white/5 backdrop-blur-md px-4 py-3"
							>
								<MonitorSmartphone className="h-5 w-5 shrink-0 text-white/60" />
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">
										{[device.os, device.name].filter(Boolean).join(" · ") || "Unknown device"}
									</p>
									<p className="text-xs text-white/50">Last active {lastUsedText(device)}</p>
								</div>
								<Button
									size="sm"
									disabled={evictingId !== null}
									onClick={() => void handleEvict(device)}
									className="shrink-0 rounded-full border border-white/20 bg-white/10 text-white hover:bg-white/20"
								>
									{evictingId === device.id ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										"Sign out"
									)}
								</Button>
							</div>
						))}
					</div>
				)}

				{evictedCount > 0 && (
					<Button
						onClick={continueToSignIn}
						className="w-full rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
					>
						Continue to sign in
					</Button>
				)}

				<p className="text-xs text-center text-white/50">
					Signed-out devices are removed immediately and can be re-added
					by signing in from them again.
				</p>
			</DialogContent>
		</Dialog>
	)
}
